// TCA-26102206: submission facts only. No transport, credentials or retry loop.
import { freezeDeliveryFields, buildDeliveryConfirmationForm, buildDeliveryMediaForm, buildDeliveryEditPayload, dataUrlToBlob } from './deliveryFormPayload.js';
import { PHOTO_PREPARATION_POLICY, validatePreparationBatch } from './prepareDeliveryPhoto.js';
import { copyDeliveryFailureAttempt } from './deliveryFailureSubmission.js';

const invalid = (message = 'Saved delivery attempt needs review; nothing was sent.') => { throw new Error(message); };
const checkSignal = (signal) => { if (signal?.aborted) throw new DOMException('Preparation retired', 'AbortError'); };

export function selectDeliveryPhotoBytes(fields, now) {
  const photos = (fields.photos || []).map((photo) => {
    if (photo.useOriginal === true && photo.preparationError && !photo.prepared) return { file: photo.file };
    const prepared = photo.prepared;
    if (!prepared || prepared.policy !== PHOTO_PREPARATION_POLICY || !(prepared.file instanceof Blob)
        || prepared.file.type !== 'image/jpeg' || !Number.isInteger(prepared.width) || !Number.isInteger(prepared.height)
        || prepared.width < 1 || prepared.height < 1 || Math.max(prepared.width, prepared.height) > 2048) {
      invalid('Every photo needs a prepared copy or an explicit original-upload choice.');
    }
    return { file: prepared.file };
  });
  validatePreparationBatch(photos.map((photo) => photo.file));
  // Original/prepared pairs stay in the draft, not in this frozen request.
  return freezeDeliveryFields({ ...fields, photos, video: fields.video ? { file: fields.video.file } : null }, now);
}

async function descriptor(blob, signal) {
  if (!blob) return null;
  if (!(blob instanceof Blob) || !blob.size || blob.size > 100 * 1024 * 1024) invalid('Media exceeds the supported size.');
  checkSignal(signal);
  const bytes = await blob.arrayBuffer();
  checkSignal(signal);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  checkSignal(signal);
  return Object.freeze({ sha256: Array.from(new Uint8Array(digest), (n) => n.toString(16).padStart(2, '0')).join(''),
    size: blob.size, content_type: blob.type || 'application/octet-stream' });
}

// Also used when reading structured-cloned state: whitelist facts, never tokens.
export function copyDeliveryPhotoAttempt(value) {
  if (value == null) return null;
  if (value.kind === 'failure') return copyDeliveryFailureAttempt(value);
  if (value.version !== 1 || !['confirmation', 'amendment'].includes(value.kind) || typeof value.captureId !== 'string' || !/^[A-Za-z0-9_-]{1,128}$/.test(value.captureId)
      || !['ready', 'pending', 'unknown', 'rejected', 'accepted'].includes(value.status)) invalid();
  const source = value.fields;
  if (!source || typeof source.receivedBy !== 'string' || !source.receivedBy.trim()
      || typeof source.receivedByRelationship !== 'string' || typeof source.notes !== 'string'
      || typeof source.deliveryTimeIso !== 'string' || !/Z$|[+-]\d\d:\d\d$/.test(source.deliveryTimeIso)
      || !Number.isFinite(Date.parse(source.deliveryTimeIso)) || !Array.isArray(source.photos)) invalid();
  if (source.signatureData != null && (typeof source.signatureData !== 'string' || source.signatureData.length > 14 * 1024 * 1024 || !source.signatureData.startsWith('data:'))) invalid();
  const location = source.gpsLocation;
  if (location && (!Number.isFinite(location.latitude) || Math.abs(location.latitude) > 90
      || !Number.isFinite(location.longitude) || Math.abs(location.longitude) > 180
      || (location.accuracy != null && (!Number.isFinite(location.accuracy) || location.accuracy < 0)))) invalid();
  const fields = freezeDeliveryFields({ receivedBy: source.receivedBy, receivedByRelationship: source.receivedByRelationship,
    notes: source.notes, deliveryTimeIso: source.deliveryTimeIso, signatureData: source.signatureData || null,
    gpsLocation: location ? { latitude: location.latitude, longitude: location.longitude, accuracy: location.accuracy } : null,
    photos: source.photos.map((photo) => ({ file: photo.file })), video: source.video ? { file: source.video.file } : null });
  validatePreparationBatch(fields.photos.map((photo) => photo.file));
  const manifest = value.manifest;
  if (!manifest || manifest.version !== 1 || !Array.isArray(manifest.photos) || manifest.photos.length !== fields.photos.length) invalid();
  const copyDescriptor = (item, blob, limit) => {
    if (!blob) { if (item !== null) invalid(); return null; }
    if (!(blob instanceof Blob) || !item || !/^[a-f0-9]{64}$/.test(item.sha256 || '')
        || !Number.isInteger(item.size) || item.size <= 0 || item.size > limit || item.size !== blob.size
        || item.content_type !== (blob.type || 'application/octet-stream')
        || !/^[a-z0-9!#$&^_.+-]+\/[a-z0-9!#$&^_.+-]+$/.test(item.content_type)) invalid();
    return Object.freeze({ sha256: item.sha256, size: item.size, content_type: item.content_type });
  };
  const copied = Object.freeze({ version: 1,
    signature: copyDescriptor(manifest.signature, fields.signatureData ? dataUrlToBlob(fields.signatureData) : null, 10 * 1024 * 1024),
    photos: Object.freeze(manifest.photos.map((item, i) => copyDescriptor(item, fields.photos[i].file, 10 * 1024 * 1024))),
    video: copyDescriptor(manifest.video, fields.video?.file, 100 * 1024 * 1024) });
  let amendment;
  if (value.kind === 'amendment') {
    const a = value.amendment;
    if (!a || !/^[a-f0-9]{64}$/.test(a.expectedRevision || '') || typeof a.textRequired !== 'boolean'
        || (a.mediaRevision !== null && !/^[a-f0-9]{64}$/.test(a.mediaRevision || ''))
        || (!a.textRequired && a.mediaRevision !== a.expectedRevision) || copied.signature
        || (!a.textRequired && !copied.photos.length && !copied.video)) invalid();
    amendment = Object.freeze({ expectedRevision: a.expectedRevision, textRequired: a.textRequired, mediaRevision: a.mediaRevision });
  }
  let receiptPhases;
  if (value.receiptPhases !== undefined) {
    const names = value.kind === 'confirmation' ? ['core', 'video'] : ['text', 'media'];
    const phases = value.receiptPhases;
    if (!phases || typeof phases !== 'object' || Array.isArray(phases)
        || Object.keys(phases).some((key) => !names.includes(key)) || names.some((key) => typeof phases[key] !== 'boolean')) invalid();
    if (value.kind === 'confirmation' ? (phases.video && (!phases.core || !copied.video))
      : ((phases.text && (!amendment.textRequired || !amendment.mediaRevision))
        || (phases.media && ((!copied.photos.length && !copied.video) || (amendment.textRequired && !phases.text))))) invalid();
    receiptPhases = Object.freeze(Object.fromEntries(names.map((name) => [name, phases[name]])));
  }
  return Object.freeze({ version: 1, kind: value.kind, captureId: value.captureId, status: value.status,
    fields, manifest: copied, ...(amendment ? { amendment } : {}), ...(receiptPhases ? { receiptPhases } : {}) });
}

async function manifestFor(fields, signal) {
  if (fields.signatureData && (typeof fields.signatureData !== 'string' || fields.signatureData.length > 14 * 1024 * 1024)) invalid();
  const signature = fields.signatureData ? dataUrlToBlob(fields.signatureData) : null;
  if (signature && signature.size > 10 * 1024 * 1024) invalid();
  if (fields.video && (!(fields.video.file instanceof Blob) || !fields.video.file.size || fields.video.file.size > 100 * 1024 * 1024)) invalid();
  const photos = [];
  // Bound peak hashing demand: never hold ten photo ArrayBuffers concurrently.
  for (const photo of fields.photos) photos.push(await descriptor(photo.file, signal));
  return { version: 1, signature: await descriptor(signature, signal),
    photos, video: await descriptor(fields.video?.file, signal) };
}

export async function createDeliveryPhotoAttempt(fields, { signal, now } = {}) {
  checkSignal(signal);
  const selected = selectDeliveryPhotoBytes(fields, now);
  const manifest = await manifestFor(selected, signal);
  checkSignal(signal);
  return copyDeliveryPhotoAttempt({ version: 1, kind: 'confirmation', captureId: crypto.randomUUID(), status: 'ready', fields: selected, manifest });
}

export async function buildIdentifiedDeliveryForm(value, { phase = 'core', signal } = {}) {
  const attempt = copyDeliveryPhotoAttempt(value);
  if (!attempt || attempt.kind !== 'confirmation' || !['core', 'video'].includes(phase) || (phase === 'video' && !attempt.fields.video)) invalid();
  if (attempt.status === 'accepted' || attempt.receiptPhases?.[phase]) invalid('This phase was already accepted; nothing was resent.');
  checkSignal(signal);
  // Structured-cloned bytes must still match their frozen manifest on retry.
  const actual = await manifestFor(attempt.fields, signal);
  if (JSON.stringify(actual) !== JSON.stringify(attempt.manifest)) invalid('Saved photo bytes no longer match their attempt. Nothing was sent.');
  const form = phase === 'core' ? buildDeliveryConfirmationForm(attempt.fields)
    : buildDeliveryMediaForm({ photos: [], video: attempt.fields.video });
  form.append('capture_id', attempt.captureId);
  form.append('attempt_manifest', JSON.stringify(attempt.manifest));
  return form;
}

// A receipt lookup is observation only. Even "not found" does not authorize an
// automatic resend: the previous request may still be committing.
export function readDeliveryPhotoReceipt(value, orderId, result) {
  const attempt = copyDeliveryPhotoAttempt(value);
  if (!attempt || attempt.kind !== 'confirmation' || result?.version !== 1 || result.order_id !== orderId || result.capture_id !== attempt.captureId
      || typeof result.video_outstanding !== 'boolean') invalid('Unexpected delivery receipt; keep the saved attempt for review.');
  const receipt = (item, phase) => {
    if (item === null) return null;
    if (!item || item.version !== 1 || item.capture_id !== attempt.captureId || item.phase !== phase
        || typeof item.accepted_at !== 'string' || !Number.isFinite(Date.parse(item.accepted_at))
        || item.result?.order_id !== orderId
        || (phase === 'core' ? item.result?.status !== 'delivered' : item.result?.added !== 1)) invalid('Unexpected delivery acknowledgement; keep the saved attempt for review.');
    return item;
  };
  const core = receipt(result.core, 'core'); const video = receipt(result.video, 'video');
  const expectedVideo = !!attempt.manifest.video;
  if ((!core && video) || (video && !expectedVideo)
      || result.video_outstanding !== !!(core && expectedVideo && !video)) invalid();
  const state = !core ? 'not-found' : expectedVideo && !video ? 'video-outstanding' : 'accepted';
  if ((attempt.receiptPhases?.core && !core) || (attempt.receiptPhases?.video && !video)
      || (attempt.status === 'accepted' && state !== 'accepted')) invalid('Previously accepted delivery receipt is unavailable. Keep the saved attempt.');
  return Object.freeze({ state, captureId: attempt.captureId,
    attempt: copyDeliveryPhotoAttempt({ ...attempt, status: state === 'accepted' ? 'accepted' : attempt.status,
      receiptPhases: { core: !!core, video: !!video } }) });
}

export async function createDeliveryPhotoAmendment(fields, checkpoint, { signal } = {}) {
  checkSignal(signal);
  if (!checkpoint?.baselineText || !/^[a-f0-9]{64}$/.test(checkpoint.amendmentRevision || '')) invalid('Load the saved delivery revision before editing.');
  const selected = selectDeliveryPhotoBytes({ ...fields, signatureData: null });
  const text = buildDeliveryEditPayload(selected);
  const textRequired = Object.keys(text).some((key) => text[key] !== checkpoint.baselineText[key]);
  const manifest = await manifestFor(selected, signal);
  checkSignal(signal);
  return copyDeliveryPhotoAttempt({ version: 1, kind: 'amendment', captureId: crypto.randomUUID(), status: 'ready',
    fields: selected, manifest, amendment: { expectedRevision: checkpoint.amendmentRevision, textRequired,
      mediaRevision: textRequired ? null : checkpoint.amendmentRevision } });
}

export async function buildIdentifiedAmendment(value, phase, { signal } = {}) {
  checkSignal(signal);
  const attempt = copyDeliveryPhotoAttempt(value);
  if (attempt?.kind !== 'amendment') invalid();
  if (attempt.status === 'accepted' || attempt.receiptPhases?.[phase]) invalid('This phase was already accepted; nothing was resent.');
  if (phase === 'text') {
    if (!attempt.amendment.textRequired || attempt.amendment.mediaRevision) invalid();
    return { ...buildDeliveryEditPayload(attempt.fields), amendment_id: attempt.captureId, expected_revision: attempt.amendment.expectedRevision };
  }
  if (phase !== 'media' || !attempt.amendment.mediaRevision || (!attempt.manifest.photos.length && !attempt.manifest.video)) invalid();
  const actual = await manifestFor(attempt.fields, signal);
  if (JSON.stringify(actual) !== JSON.stringify(attempt.manifest)) invalid('Saved media no longer match their amendment. Nothing was sent.');
  const form = buildDeliveryMediaForm(attempt.fields);
  form.append('amendment_id', attempt.captureId);
  form.append('amendment_manifest', JSON.stringify({ version: 1, expected_revision: attempt.amendment.mediaRevision,
    photos: attempt.manifest.photos, video: attempt.manifest.video }));
  return form;
}

export function readDeliveryPhotoAmendment(value, orderId, result) {
  const attempt = copyDeliveryPhotoAttempt(value);
  if (attempt?.kind !== 'amendment' || result?.version !== 1 || result.order_id !== orderId || result.capture_id !== attempt.captureId) invalid();
  const receipt = (item, phase) => {
    if (item === null) return null;
    if (!item || item.version !== 1 || item.capture_id !== attempt.captureId || item.phase !== phase
        || typeof item.accepted_at !== 'string' || !Number.isFinite(Date.parse(item.accepted_at))
        || item.result?.order_id !== orderId || !/^[a-f0-9]{64}$/.test(item.result.amendment_revision || '')) invalid();
    return item.result;
  };
  const text = receipt(result.amendment_text, 'amendment_text');
  const media = receipt(result.amendment_media, 'amendment_media');
  const count = attempt.manifest.photos.length + (attempt.manifest.video ? 1 : 0);
  const required = attempt.amendment.textRequired;
  if ((text && !required) || (media && (!count || media.added !== count)) || (required && !text && media)) invalid();
  if (text) {
    const fields = buildDeliveryEditPayload(attempt.fields);
    if (Object.keys(fields).some((key) => (text.confirmation?.[key] ?? '') !== fields[key])) invalid();
    if (attempt.amendment.mediaRevision && attempt.amendment.mediaRevision !== text.amendment_revision) invalid();
  } else if (required && attempt.amendment.mediaRevision) invalid('Previously accepted text receipt is unavailable. Keep the saved amendment.');
  const state = required && !text ? 'text-outstanding' : count && !media ? 'media-outstanding' : 'accepted';
  if ((attempt.receiptPhases?.text && !text) || (attempt.receiptPhases?.media && !media)
      || (attempt.status === 'accepted' && state !== 'accepted')) invalid('Previously accepted amendment receipt is unavailable. Keep the saved amendment.');
  const next = copyDeliveryPhotoAttempt({ ...attempt, status: state === 'accepted' ? 'accepted' : attempt.status,
    receiptPhases: { text: !!text, media: !!media }, amendment: { ...attempt.amendment,
      mediaRevision: text ? text.amendment_revision : attempt.amendment.mediaRevision } });
  return { state, attempt: next };
}
